import { CommonModule } from '@angular/common';
import { Component, ElementRef, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { EnfafIconComponent } from '../enfaf-icon/enfaf-icon.component';
import { ToastService } from '../../core/services/toast/toast.service';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs-dist/build/pdf.worker.min.mjs';
const PDF_PREVIEW_TIMEOUT_MS = 15000;

export interface PdfFile {
  file: File;
  name: string;
  size: number;
}

export interface InitialPdfFile {
  name: string;
  url?: string;
  thumbnail?: string;
}

@Component({
  selector: 'enfaf-pdf-upload',
  standalone: true,
  imports: [CommonModule, EnfafIconComponent],
  templateUrl: './enfaf-pdf-upload.component.html',
  styleUrl: './enfaf-pdf-upload.component.scss'
})
export class EnfafPdfUploadComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  // Inputs
  readonly maxFileSize = input<number>(50 * 1024 * 1024); // 50MB default
  readonly accept = input<string>('application/pdf');
  readonly uploadText = input<string>('Subir PDF');
  readonly title = input<string>(''); // Title to display
  readonly fileName = input<string>(''); // Custom filename to display
  readonly initialFile = input<InitialPdfFile | null>(null); // PDF file already uploaded (edit mode)
  readonly minHeight = input<string>('200px');
  readonly minWidth = input<string>('220px');
  readonly width = input<string>('220px');

  constructor(private toast: ToastService) {}

  async ngOnInit(): Promise<void> {
    const initial = this.initialFile();
    if (initial) {
      this.hasInitialFile.set(true);
      this.showingInitialFile.set(true);
      // Load thumbnail for initial file
      if (initial.thumbnail) {
        this.pdfPreviewSrc.set(initial.thumbnail);
      } else if (initial.url) {
        // If no thumbnail but has URL, try to generate preview
        await this.loadPdfPreviewFromUrl(initial.url);
      }
    }
  }

  // Outputs
  readonly fileSelected = output<PdfFile>();
  readonly fileRemoved = output<void>();
  readonly error = output<string>();
  readonly fileChanged = output<boolean>(); // Emits true when file changes from initial

  // State
  readonly selectedFile = signal<PdfFile | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly isDragging = signal<boolean>(false);
  readonly hasInitialFile = signal<boolean>(false);
  readonly showingInitialFile = signal<boolean>(false);
  readonly pdfPreviewSrc = signal<string | null>(null);
  readonly isUploadingPdf = signal<boolean>(false);

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  /**
   * Handle drop event
   */
  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      await this.processFile(files[0]);
    }
  }

  /**
   * Trigger file input click
   */
  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Handle file selection
   */
  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      await this.processFile(input.files[0]);
      this.resetInput(input);
    }
  }

  /**
   * Process selected or dropped file
   */
  private async processFile(file: File): Promise<void> {
    // Validate file type
    if (file.type !== this.accept()) {
      const error = 'Solo se permiten archivos PDF';
      this.errorMessage.set(error);
      this.error.emit(error);
      this.toast.presentErrorToast(error);
      return;
    }

    // Validate file size
    if (file.size > this.maxFileSize()) {
      const maxSizeMB = Math.round(this.maxFileSize() / (1024 * 1024));
      const error = `El archivo no puede superar los ${maxSizeMB}MB`;
      this.errorMessage.set(error);
      this.error.emit(error);
      this.toast.presentErrorToast(error);
      return;
    }

    // Validate file is not empty
    if (file.size === 0) {
      const error = 'El archivo no puede estar vacío';
      this.errorMessage.set(error);
      this.error.emit(error);
      this.toast.presentErrorToast(error);
      return;
    }

    const pdfFile: PdfFile = {
      file,
      name: file.name,
      size: file.size
    };

    try {
      this.isUploadingPdf.set(true);

      // Generate preview from first page
      const previewSrc = await this.generatePdfPreview(file);
      this.pdfPreviewSrc.set(previewSrc);
    } catch {
      this.pdfPreviewSrc.set(null);
    } finally {
      this.selectedFile.set(pdfFile);
      this.errorMessage.set(null);
      this.showingInitialFile.set(false);
      this.fileSelected.emit(pdfFile);

      // Emit change event if there was an initial file
      if (this.hasInitialFile()) {
        this.fileChanged.emit(true);
      }

      this.isUploadingPdf.set(false);
      this.isDragging.set(false);
    }
  }

  /**
   * Reset input value to allow re-uploading
   */
  private resetInput(input: HTMLInputElement): void {
    input.value = '';
  }

  /**
   * Remove selected file
   */
  removeFile(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    // Clear everything - don't show initial file
    this.selectedFile.set(null);
    this.showingInitialFile.set(false);
    this.pdfPreviewSrc.set(null);

    // Reset states
    this.errorMessage.set(null);
    this.isUploadingPdf.set(false);
    this.isDragging.set(false);

    // Emit events
    if (this.hasInitialFile()) {
      this.fileChanged.emit(true); // Signal that file was removed from initial
    }
    this.fileRemoved.emit();

    // Reset input to allow re-uploading
    if (this.fileInput?.nativeElement) {
      this.resetInput(this.fileInput.nativeElement);
    }
  }

  /**
   * Get display file name
   */
  getDisplayFileName(): string {
    // If user selected a new file, show its real name
    if (this.selectedFile()) {
      return this.selectedFile()!.name;
    }

    // If showing initial file and custom name provided, use custom name
    if (this.showingInitialFile() && this.fileName()) {
      return `${this.fileName()}.pdf`;
    }

    // Otherwise show initial file name
    if (this.showingInitialFile() && this.initialFile()) {
      return this.initialFile()!.name;
    }

    return '';
  }

  /**
   * Check if showing any file
   */
  hasFile(): boolean {
    return this.selectedFile() !== null || this.showingInitialFile();
  }

  /**
   * Check if current file is new (not uploaded yet)
   */
  isNewFile(): boolean {
    return this.selectedFile() !== null;
  }

  /**
   * Generate PNG preview from first page of PDF file
   */
  private async generatePdfPreview(file: File): Promise<string> {
    const previewPromise = new Promise<string>((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.onload = async (e) => {
        try {
          const typedArray = new Uint8Array(e.target?.result as ArrayBuffer);
          const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
          const page = await pdf.getPage(1);

          // Set scale for good quality preview
          const scale = 2;
          const viewport = page.getViewport({ scale });

          // Create canvas
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          // Render PDF page to canvas
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
            canvas: canvas
          };

          await page.render(renderContext).promise;

          // Convert canvas to data URL
          const dataUrl = canvas.toDataURL('image/png');
          resolve(dataUrl);
        } catch (error) {
          reject(error);
        }
      };

      fileReader.onerror = () => {
        reject(new Error('Error reading file'));
      };

      fileReader.readAsArrayBuffer(file);
    });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('PDF preview timed out')), PDF_PREVIEW_TIMEOUT_MS);
    });

    try {
      return await Promise.race([previewPromise, timeoutPromise]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  /**
   * Load PDF preview from URL (for initial files from backend)
   */
  private async loadPdfPreviewFromUrl(url: string): Promise<void> {
    try {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const page = await pdf.getPage(1);

      const scale = 2;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        canvas: canvas
      };

      await page.render(renderContext).promise;
      const dataUrl = canvas.toDataURL('image/png');
      this.pdfPreviewSrc.set(dataUrl);
    } catch (error) {
      console.error('Error loading PDF preview from URL:', error);
      // Don't show error to user, just don't show preview
    }
  }
}
